/**
 * /u/{handle} — Instagram-style public profile page.
 * Displays avatar, name + handle, profession, intro, socials, and the
 * user's visible posts grid. Own-account viewers see an Edit Profile button.
 */
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  MapPin, Globe, Facebook, Instagram, Youtube, Linkedin,
  Music2, ExternalLink, MessageCircle, Share2, Copy, Pencil, User,
  UserPlus, Check, Download, Lock, Users, GraduationCap, Briefcase,
  Sparkles, Award, Image as ImageIcon,
} from "lucide-react";
import Avatar from "@/components/Avatar";
import SocialPulseVerifiedBadge from "@/components/SocialPulseVerifiedBadge";

const SOCIAL_ICONS = [
  { key: "facebook_url", Icon: Facebook, label: "Facebook", color: "#1877F2" },
  { key: "instagram_url", Icon: Instagram, label: "Instagram", color: "#E4405F" },
  { key: "youtube_url", Icon: Youtube, label: "YouTube", color: "#FF0000" },
  { key: "linkedin_url", Icon: Linkedin, label: "LinkedIn", color: "#0A66C2" },
  { key: "twitter_url", Icon: () => <span style={{ fontFamily: "serif", fontSize: 18, lineHeight: 1 }}>𝕏</span>, label: "X / Twitter", color: "#111" },
  { key: "tiktok_url", Icon: Music2, label: "TikTok", color: "#000" },
];

const TYPE_LINKS = {
  causes: (d) => `/causes/${d.slug || d.id}`,
  appeals: (d) => `/offer-help/${d.slug || d.id}`,
  help_offers: (d) => `/find-help/${d.slug || d.id}`,
  family_trees: (d) => `/family/${d.slug || d.id}`,
  sp_articles: (d) => `/articles/${d.slug}`,
  events: (d) => `/events/${d.slug || d.id}`,
  memorial_nominations: (d) => `/celebrate/${d.slug || d.id}`,
};

const TYPE_LABELS = {
  causes: "Cause", appeals: "Appeal", help_offers: "Help", family_trees: "Family",
  sp_articles: "Article", events: "Event", memorial_nominations: "Memorial",
  family_members: "Family", family_events: "Family event", family_albums: "Album",
};

export default function PublicProfile() {
  const { handle } = useParams();
  const { user: me } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, user: null, posts: [], error: null });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, user: null, posts: [], error: null });
    http.get(`/profiles/${handle}`)
      .then((r) => { if (alive) setState({ loading: false, ...r.data, error: null }); })
      .catch((e) => { if (alive) setState({ loading: false, user: null, posts: [], error: e?.response?.data?.detail || "Profile not found." }); });
    return () => { alive = false; };
  }, [handle]);

  const isMe = me && state.user && me.id === state.user.id;
  const avatar = state.user?.avatar_url || state.user?.profile_picture;
  const socials = SOCIAL_ICONS.filter((s) => state.user?.[s.key]);
  const otherLinks = state.user?.other_links || [];

  const shareProfile = async () => {
    const url = `${window.location.origin}/u/${state.user.handle}`;
    // Try modern clipboard API, fall back to a hidden textarea (works in every browser + iframe).
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Profile link copied");
    } catch {
      // Last resort: show the URL for manual copy.
      window.prompt("Copy this profile link:", url);
    }
  };

  const contactUser = () => {
    if (!me) {
      nav(`/login?next=/u/${handle}`);
      return;
    }
    nav(`/messages?to=${state.user.id}`);
  };

  const [connectStatus, setConnectStatus] = useState("idle"); // idle | sending | sent | already
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectMsg, setConnectMsg] = useState("");
  const openConnectModal = () => {
    if (!me) return nav(`/login?next=/u/${handle}`);
    if (!state.user?.id) return;
    setConnectMsg("");
    setConnectOpen(true);
  };
  const requestConnect = async () => {
    if (!me) return nav(`/login?next=/u/${handle}`);
    if (!state.user?.id) return;
    setConnectStatus("sending");
    try {
      const { data } = await http.post("/connections/request", {
        to_user_id: state.user.id,
        message: connectMsg.trim() || undefined,
      });
      setConnectStatus(data.status === "sent" ? "sent" : "already");
      setConnectOpen(false);
      toast.success(data.status === "sent" ? "Connection request sent" : "Already connected / pending");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send request");
      setConnectStatus("idle");
    }
  };

  const downloadResume = () => {
    if (!state.user?.handle) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/profile/resume/${state.user.handle}.pdf`;
    // Open in a new tab — WeasyPrint returns application/pdf which the browser previews and offers "Save As".
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (state.loading) return <div className="max-w-4xl mx-auto px-6 py-16 text-center" data-testid="public-profile-loading">Loading…</div>;
  if (state.error) {
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="public-profile-error">
        <User className="w-14 h-14 mx-auto opacity-30 mb-6" />
        <h1 className="font-display text-3xl">Profile not found</h1>
        <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>{state.error}</p>
        <Link to="/" className="wofy-btn-primary mt-8 inline-flex">Back to home</Link>
      </div>
    );
  }

  const u = state.user;
  const location = [u.city, u.state, u.country].filter(Boolean).join(", ");
  const isPrivate = !!state.private;

  // ---------------- Private-card mode ----------------
  if (isPrivate) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="public-profile-private">
        <div className="wofy-card !p-8 text-center">
          <div className="flex justify-center mb-5">
            <Avatar src={avatar} name={u.name} gender={u.gender} size={140} testId="private-avatar" />
          </div>
          <h1 className="font-display text-3xl">{u.name}</h1>
          {u.handle && <div className="text-sm font-mono opacity-60 mt-1">@{u.handle}</div>}
          {u.profession && (
            <p className="mt-3 text-base font-medium" style={{ color: "var(--wofy-primary)" }}>{u.profession}</p>
          )}
          {location && (
            <div className="text-sm mt-2 inline-flex items-center gap-1" style={{ color: "var(--wofy-text-soft)" }}>
              <MapPin className="w-4 h-4" /> {location}
            </div>
          )}
          {u.about && (
            <p className="mt-4 text-sm max-w-md mx-auto" style={{ color: "var(--wofy-text-soft)" }}>{u.about}</p>
          )}
          <div className="mt-6 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest opacity-60">
            <Lock className="w-3 h-3" /> This profile is private
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-2 justify-center">
            <button onClick={requestConnect} disabled={connectStatus === "sending" || connectStatus === "sent" || connectStatus === "already"} className="wofy-btn-primary text-sm" data-testid="private-connect-btn">
              {connectStatus === "sent" || connectStatus === "already" ? <Check className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
              {connectStatus === "sent" ? "Request sent" : connectStatus === "already" ? "Pending / connected" : connectStatus === "sending" ? "Sending…" : "Request to connect"}
            </button>
            <button onClick={contactUser} className="wofy-btn-ghost text-sm" data-testid="private-message-btn">
              <MessageCircle className="w-3.5 h-3.5" /> Message
            </button>
          </div>
        </div>
        <div className="text-center mt-4">
          <Link to="/members" className="text-xs underline opacity-70">Back to Our Members</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-14" data-testid="public-profile-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-10">
        <div className="shrink-0">
          <Avatar src={avatar} name={u.name} gender={u.gender} size={160} testId="profile-avatar" className="border-4" />
        </div>

        <div className="flex-1 min-w-0 text-center sm:text-left">
          <div className="flex flex-wrap items-center gap-3 justify-center sm:justify-start">
            <h1 className="font-display text-3xl sm:text-4xl">{u.name}</h1>
            <SocialPulseVerifiedBadge user={u} size="md" showLabel />
            <span className="text-sm font-mono" style={{ color: "var(--wofy-text-soft)" }} data-testid="profile-handle">
              @{u.handle}
            </span>
          </div>
          {u.profession && (
            <p className="mt-1 text-sm font-medium" style={{ color: "var(--wofy-primary)" }} data-testid="profile-profession">
              {u.profession}
            </p>
          )}
          {u.intro && (
            <p className="mt-2 text-sm leading-relaxed" data-testid="profile-intro">
              {u.intro}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 justify-center sm:justify-start text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            {location && (
              <span className="flex items-center gap-1" data-testid="profile-location">
                <MapPin className="w-3.5 h-3.5" /> {location}
              </span>
            )}
            {u.website && (
              <a
                href={u.website}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="profile-website"
                className="flex items-center gap-1 hover:underline"
                style={{ color: "var(--wofy-primary)" }}
              >
                <Globe className="w-3.5 h-3.5" /> {u.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
              </a>
            )}
          </div>

          {/* Action buttons */}
          <div className="mt-5 flex flex-wrap items-center gap-2 justify-center sm:justify-start">
            {isMe ? (
              <>
                <Link to="/profile/edit" className="wofy-btn-primary text-sm" data-testid="profile-edit-btn">
                  <Pencil className="w-3.5 h-3.5" /> Edit Profile
                </Link>
                <Link to="/profile/resume-studio" className="wofy-btn-ghost text-sm" data-testid="profile-resume-studio-btn" title="AI-assisted resume drafting">
                  <Sparkles className="w-3.5 h-3.5" /> Resume Studio
                </Link>
                <button onClick={downloadResume} className="wofy-btn-ghost text-sm" data-testid="profile-resume-btn" title="Download as PDF">
                  <Download className="w-3.5 h-3.5" /> Download Resume
                </button>
              </>
            ) : (
              <>
                <button onClick={openConnectModal} disabled={connectStatus === "sending" || connectStatus === "sent" || connectStatus === "already"} className="wofy-btn-primary text-sm" data-testid="profile-connect-btn">
                  {connectStatus === "sent" || connectStatus === "already" ? <Check className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
                  {connectStatus === "sent" ? "Request sent" : connectStatus === "already" ? "Pending / connected" : "Connect"}
                </button>
                <button onClick={contactUser} className="wofy-btn-ghost text-sm" data-testid="profile-contact-btn">
                  <MessageCircle className="w-3.5 h-3.5" /> Message
                </button>
              </>
            )}
            <button onClick={shareProfile} className="wofy-btn-ghost text-sm" data-testid="profile-share-btn">
              <Share2 className="w-3.5 h-3.5" /> Share Profile
            </button>
          </div>

          {/* Socials */}
          {(socials.length > 0 || otherLinks.length > 0) && (
            <div className="mt-5 flex flex-wrap items-center gap-2 justify-center sm:justify-start" data-testid="profile-socials">
              {socials.map((s) => {
                const Icon = s.Icon;
                return (
                  <a
                    key={s.key}
                    href={u[s.key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={s.label}
                    data-testid={`profile-social-${s.key}`}
                    className="w-9 h-9 rounded-full flex items-center justify-center border transition hover:scale-110"
                    style={{ borderColor: "var(--wofy-border)", color: s.color }}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
              {otherLinks.map((l, i) => (
                <a
                  key={`other-${i}`}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={l.label || l.url}
                  data-testid={`profile-other-${i}`}
                  className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs hover:bg-[var(--wofy-surface)]"
                  style={{ borderColor: "var(--wofy-border)" }}
                >
                  <ExternalLink className="w-3 h-3" /> {l.label || "Link"}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Extended CV sections */}
      <CVSections user={u} />

      {/* Post grid */}
      <div className="wofy-divider my-10" />
      <div className="flex items-center justify-between mb-4">
        <h2 className="overline">POSTS · {state.post_count || state.posts.length}</h2>
      </div>
      {state.posts.length === 0 ? (
        <div className="py-16 text-center rounded-xl border-2 border-dashed" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
          {isMe ? "You haven't shared anything public yet. Post a story or make one of your posts visible on your profile." : `${u.name} hasn't shared anything public yet.`}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3" data-testid="profile-posts-grid">
          {state.posts.map((p) => {
            const linkFn = TYPE_LINKS[p.content_type];
            const url = linkFn ? linkFn(p) : "#";
            return (
              <Link
                key={`${p.content_type}-${p.id}`}
                to={url}
                data-testid={`profile-post-${p.id}`}
                className="relative aspect-square rounded-lg overflow-hidden bg-[var(--wofy-surface)] group"
              >
                {p.image ? (
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover transition group-hover:scale-105" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center p-3 text-center">
                    <div>
                      <div className="text-[9px] uppercase font-mono tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
                        {TYPE_LABELS[p.content_type] || p.content_type}
                      </div>
                      <div className="mt-1 text-xs font-semibold line-clamp-3">{p.title || "(untitled)"}</div>
                    </div>
                  </div>
                )}
                {p.image && (
                  <div className="absolute inset-0 bg-gradient-to-t from-[#FDFBF7]/95 via-[#FDFBF7]/40 to-transparent opacity-0 group-hover:opacity-100 transition flex items-end p-2">
                    <div>
                      <div className="text-[9px] uppercase font-mono tracking-wider" style={{ color: "var(--wofy-primary)" }}>
                        {TYPE_LABELS[p.content_type] || p.content_type}
                      </div>
                      <div className="text-xs font-semibold line-clamp-2" style={{ color: "var(--wofy-text)" }}>{p.title || "(untitled)"}</div>
                    </div>
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}

      {/* Connection request modal */}
      {connectOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.5)" }}
          data-testid="connect-modal"
          onClick={() => setConnectOpen(false)}
        >
          <div
            className="wofy-card max-w-md w-full !p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-2xl mb-1">Connect with {state.user?.name?.split(" ")[0] || "them"}</h3>
            <p className="text-sm mb-4" style={{ color: "var(--wofy-text-soft)" }}>
              Add a short note (optional). Once accepted, you can message each other directly.
            </p>
            <textarea
              className="wofy-input w-full"
              rows={4}
              maxLength={300}
              placeholder="Hi! I saw your profile and would love to connect…"
              value={connectMsg}
              onChange={(e) => setConnectMsg(e.target.value)}
              data-testid="connect-modal-message"
            />
            <div className="text-[10px] font-mono opacity-60 text-right mt-1">{connectMsg.length}/300</div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConnectOpen(false)}
                className="wofy-btn-ghost text-sm"
                data-testid="connect-modal-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={requestConnect}
                disabled={connectStatus === "sending"}
                className="wofy-btn-primary text-sm"
                data-testid="connect-modal-send"
              >
                {connectStatus === "sending" ? "Sending…" : "Send request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Extended CV Sections ----------

function CVSections({ user }) {
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const about = user.about || "";
  const showMore = about.length > 250;
  const aboutDisplay = aboutExpanded ? about : about.slice(0, 250) + (showMore ? "…" : "");

  const publicPhotos = (user.photos || []).filter((p) => p.public);
  const edu = user.education || [];
  const career = user.career || [];
  const skills = user.skills || [];
  const achievements = user.achievements || [];

  const empty = !about && publicPhotos.length === 0 && edu.length === 0 && career.length === 0 && skills.length === 0 && achievements.length === 0;
  if (empty) return null;

  return (
    <div className="mt-12 space-y-10" data-testid="profile-cv-sections">
      {about && (
        <Section icon={User} title="About">
          <p className="text-sm leading-relaxed whitespace-pre-line" data-testid="profile-about">{aboutDisplay}</p>
          {showMore && (
            <button onClick={() => setAboutExpanded((v) => !v)} className="mt-3 text-xs font-mono uppercase tracking-widest underline" style={{ color: "var(--wofy-primary)" }} data-testid="profile-about-toggle">
              {aboutExpanded ? "Show less" : "More →"}
            </button>
          )}
        </Section>
      )}

      {publicPhotos.length > 0 && (
        <Section icon={ImageIcon} title={`Photos (${publicPhotos.length})`}>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
            {publicPhotos.map((p) => (
              <a key={p.id} href={p.url ? (p.url.startsWith("http") ? p.url : p.url) : "#"} target="_blank" rel="noopener noreferrer" className="aspect-square rounded-lg overflow-hidden bg-black block">
                <img src={p.url ? (p.url.startsWith("http") ? p.url : p.url) : ""} alt={p.caption || "Photo"} className="w-full h-full object-cover hover:scale-105 transition" />
              </a>
            ))}
          </div>
        </Section>
      )}

      {career.length > 0 && (
        <Section icon={Briefcase} title="Career / Experience">
          <div className="space-y-4">
            {career.map((c, i) => (
              <div key={c.id || i} className="border-l-2 pl-4" style={{ borderColor: "var(--wofy-primary)" }}>
                <div className="font-semibold">{c.name}</div>
                {(c.from_ym || c.to_ym) && (
                  <div className="text-xs font-mono opacity-70 mt-0.5">{c.from_ym || "—"} → {c.to_ym || "Present"}</div>
                )}
                {c.description && <p className="text-sm mt-2 whitespace-pre-line" style={{ color: "var(--wofy-text-soft)" }}>{c.description}</p>}
                {c.notes && <p className="text-xs mt-1 italic opacity-70">{c.notes}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {edu.length > 0 && (
        <Section icon={GraduationCap} title="Education">
          <div className="space-y-4">
            {edu.map((e, i) => (
              <div key={e.id || i} className="border-l-2 pl-4" style={{ borderColor: "#8b5cf6" }}>
                <div className="font-semibold">{e.school}</div>
                {e.area && <div className="text-sm mt-0.5">{e.area}</div>}
                {(e.from_ym || e.to_ym) && (
                  <div className="text-xs font-mono opacity-70 mt-0.5">{e.from_ym || "—"} → {e.to_ym || "Present"}</div>
                )}
                {e.notes && <p className="text-sm mt-2 whitespace-pre-line" style={{ color: "var(--wofy-text-soft)" }}>{e.notes}</p>}
                <div className="flex flex-wrap gap-3 mt-2 text-xs">
                  {e.website && <a href={e.website} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "var(--wofy-primary)" }}>Website</a>}
                  {e.linkedin && <a href={e.linkedin} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "var(--wofy-primary)" }}>LinkedIn</a>}
                  {e.youtube && <a href={e.youtube} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "var(--wofy-primary)" }}>YouTube</a>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {skills.length > 0 && (
        <Section icon={Sparkles} title="Skills / Talents">
          <div className="grid sm:grid-cols-2 gap-3">
            {skills.map((s, i) => (
              <div key={s.id || i} className="wofy-card !p-4">
                <div className="font-semibold text-sm">{s.name}</div>
                {s.description && <p className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>{s.description}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {achievements.length > 0 && (
        <Section icon={Award} title="Achievements / Awards">
          <div className="space-y-3">
            {achievements.map((a, i) => (
              <div key={a.id || i} className="flex gap-4 items-start">
                <div className="w-14 text-center shrink-0">
                  <div className="font-display text-lg" style={{ color: "var(--wofy-primary)" }}>{a.year}</div>
                  {a.month && <div className="text-[10px] font-mono opacity-60">{String(a.month).padStart(2, "0")}</div>}
                </div>
                <div>
                  <div className="font-semibold text-sm">{a.title}</div>
                  {a.description && <p className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>{a.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />}
        <h2 className="overline">{title}</h2>
      </div>
      {children}
    </section>
  );
}
